import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cotera/client/v0/components/ui/command';
import { useDatasetShape } from '@cotera/client/app/v1/trigger/hooks';
import { cn } from '@cotera/client/v0/lib/utils';
import { TOOL_INPUT_CONTROL_HEIGHT } from '../tool-input-styles';
import type { ToolInputControlProps } from './index';

/** The saved value read back as a string[], tolerating a partly-typed param. */
const readColumnNames = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((name): name is string => typeof name === 'string')
    : [];

/**
 * Picks which of a dataset's columns a read returns.
 *
 * The field is `string[] | null`, which the generic editor renders as a JSON
 * box — so choosing two of a dataset's columns meant typing `["a", "b"]` with
 * the names spelled from memory, and a typo read as a column that is not there.
 * The names come from the same live shape the trigger form and the write
 * builder use, so what can be picked here is what the dataset actually has.
 *
 * Unset is the tool's own default: `columns: null` returns every column. So an
 * empty selection is "all of them" and says so, rather than reading as a field
 * nobody got round to filling in.
 *
 * A saved name the dataset no longer has still shows as a chip rather than
 * disappearing — the column was dropped or renamed elsewhere, and silently
 * editing the node would hide the reason its next run returns less than it did.
 */
export const DatasetColumnsControl: React.FC<ToolInputControlProps> = ({
  field,
  params,
  setValues,
}) => {
  const [open, setOpen] = useState(false);
  const rawDatasetId = params['datasetId'];
  const datasetId =
    typeof rawDatasetId === 'string' && rawDatasetId !== ''
      ? rawDatasetId
      : null;
  const shape = useDatasetShape(datasetId);
  const columns = shape?.columns ?? [];
  const selected = readColumnNames(params[field.name]);
  // A name the shape no longer carries is still offered for removal, and sorted
  // to the end so it does not shuffle the dataset's own order.
  const options = [
    ...columns,
    ...selected.filter((name) => !columns.includes(name)),
  ];

  const toggle = (name: string): void => {
    const next = selected.includes(name)
      ? selected.filter((existing) => existing !== name)
      : [...selected, name];
    // `[]` would be a claim that the read should return no columns at all. The
    // tool reads an unset field as every column, which is what an empty picker
    // means, so clear the param rather than saving an empty list.
    setValues({ [field.name]: next.length === 0 ? undefined : next });
  };

  if (datasetId === null) {
    return (
      <Text.Caption className="text-muted-foreground block">
        Choose a dataset first, then pick which of its columns to return.
      </Text.Caption>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              TOOL_INPUT_CONTROL_HEIGHT,
              'w-full justify-between rounded-md font-normal'
            )}
          >
            <span className="truncate">
              {selected.length === 0
                ? 'All columns'
                : `${selected.length} of ${options.length} columns`}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-(--radix-popover-trigger-width) p-0"
        >
          <Command>
            <CommandInput placeholder="Search columns…" />
            <CommandList>
              <CommandEmpty>
                {/* "No columns" is a claim about the dataset; until the shape
                    lands the only true thing to say is that it has not. */}
                {shape === null
                  ? 'Loading this dataset’s columns…'
                  : 'This dataset has no columns yet.'}
              </CommandEmpty>
              <CommandGroup>
                {options.map((name) => (
                  <CommandItem
                    key={name}
                    value={name}
                    onSelect={() => toggle(name)}
                  >
                    <Check
                      className={
                        selected.includes(name)
                          ? 'mr-2 size-4 opacity-100'
                          : 'mr-2 size-4 opacity-0'
                      }
                    />
                    <span className="truncate">{name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((name) => (
            <Badge key={name} variant="secondary" className="gap-1 pr-1">
              <span className="truncate">{name}</span>
              <button
                type="button"
                onClick={() => toggle(name)}
                title="Remove column"
                aria-label={`Remove ${name}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
};
